<template>
    <main>
        <div class="container" v-if="submissionSuccessful">
            <h1 class="thankyou_header desktop_centered">Thank you</h1>
            <div class="thankyou_text content" v-text="thankYouText"></div>
            <router-link class="thank_you_return" :to="{ name: props.calculatorComponent }">Return to home page</router-link>
        </div>

        <div v-else>
            <div class="top_line">
                <div class="container">
                    <router-link class="back_link" :to="{ name: props.calculatorComponent }">Back</router-link>

                    <img v-if="props.logo" :src="props.logo" :alt="props.brand + ' logo'" class="brand_logo">
                <img v-if="props.brandHeader" :src="props.brandHeader" class="brand_header" :alt="props.brand">
                </div>
            </div>
            <div class="container">
                <h1 class="desktop_centered desktop_mb32">Contact us</h1>

                <div class="row">
                    <div class="contact_page_intro content content_16">
                        <p v-html="contactPageIntro" />
                    </div>

                    <div class="contact_page_form">
                        <form action="" @submit.prevent="onSubmit">
                            <h2>Make an enquiry</h2>
                            <div class="content_16" v-html="contactPageText2" />

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">Choose a vehicle:</span>
                                    <Multiselect id="model" :taggable="false" :clearable="false" :min-chars="1"
                                        :searchable="true" v-model="selectedModel" label="label" :options="modelsArray"
                                        data-test-id="select-model" data-cy='selectedModel' placeholder="Please select">
                                    </Multiselect>
                                </div>
                            </div>

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">Choose a submodel:</span>
                                    <Multiselect id="submodel" :taggable="false" :clearable="false" :min-chars="1"
                                        :searchable="true" v-model="selectedSubmodel"
                                        :options="cleanSubmodelsArray"
                                        data-test-id="select-submodel" v-on:change="(e) => submodelChange(e)"
                                        data-cy='selectedSubmodel'>
                                        <template v-slot:singlelabel="{ value }">
                                            <div class="multiselect-single-label">
                                                <span class="multiselect-single-label-text">
                                                    {{ formatCarSubmodel(value).label }} -
                                                    <b>{{ getCurrency(formatCarSubmodel(value).rrp, true) }}</b>
                                                </span>
                                            </div>
                                        </template>
                                        <template v-slot:option="{ option }">
                                            <span class="multiselect-single-label-text">
                                                {{ formatCarSubmodel(option).label }} -
                                                <b>{{ getCurrency(formatCarSubmodel(option).rrp, true) }}</b>
                                            </span>
                                        </template>
                                    </Multiselect>
                                </div>
                            </div>

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">Choose a dealer:</span>
                                    <Multiselect id="dealer" :taggable="false" :clearable="false" :min-chars="1"
                                        :searchable="true" v-model="selectedDealer" label="label" :options="dealers"
                                        data-test-id="select-dealer" data-cy='selectedDealer' placeholder="Please select">
                                    </Multiselect>
                                    <span class="input_error" v-if="showErrors && !formValidationDynamic.selectedDealer">
                                        Please choose dealer
                                    </span>
                                </div>
                            </div>

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">First name:</span>
                                    <input name="first_name" id="first_name" type="text" v-model="firstName" data-cy="firstName" />
                                    <span class="input_error" v-if="showErrors && !formValidationDynamic.firstName">
                                        Please enter your first name
                                    </span>
                                </div>
                            </div>

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">Last name:</span>
                                    <input name="last_name" id="last_name" type="text" v-model="lastName" data-cy="lastName"/>
                                    <span class="input_error" v-if="showErrors && !formValidationDynamic.lastName">
                                        Please enter your last name
                                    </span>
                                </div>
                            </div>

                            <div class="form_part">
                                <label class="input_wrap">
                                    <span class="label_text">Phone:</span>
                                    <MazPhoneNumberInput
                                        v-model="phone"
                                        color="#000"
                                        no-example
                                        country-code="NZ"
                                        no-country-selector
                                        data-cy='phone'
                                        />
                                    <span class="input_error" v-if="showErrors && !formValidationDynamic.phone">
                                        Please enter your phone
                                    </span>
                                </label>
                            </div>

                            <div class="form_part">
                                <div class="input_wrap">
                                    <span class="label_text">Email:</span>
                                    <input name="email" id="email" type="email" v-model="email" data-cy="email"/>
                                    <span class="input_error" v-if="showErrors && !formValidationDynamic.email">
                                        Please enter your email
                                    </span>
                                </div>
                            </div>

                            <div class="controls">
                                <button v-if="formIsValid" class="enquire_button"
                                    :class="{ in_progress: submissionInProgress }"
                                    :disabled="submissionSuccessful">
                                    Send enquiry
                                </button>
                                <button v-else v-on:click.prevent="() => checkForm()" class="enquire_button"
                                    :class="{ in_progress: submissionInProgress }"
                                    :disabled="submissionSuccessful">
                                    Send enquiry
                                </button>
                            </div>

                            <div class="success_message" v-if="submissionSuccessful">
                                Your enquiry submitted successfully
                            </div>
                            <input type="hidden" value="" name="reCaptchaToken" id="reCaptchaToken"
                                    class="reCaptchaToken" />
                        </form>
                    </div>
                </div>
            </div>

        </div>
    </main>
</template>

<script>
import { defineComponent, ref, onMounted, computed, toRaw, watch, nextTick, reactive } from 'vue'
import Multiselect from '@vueform/multiselect'
import MazPhoneNumberInput from 'maz-ui/components/MazPhoneNumberInput'
import axios from 'axios'
import useMixin from '../mixins/commonMixin'
import CONSTANTS from '@/data/constants'

export default defineComponent({
    name: 'ContactUs',
    components: {
        Multiselect,
        MazPhoneNumberInput
    },
    props: {
        logo: String,
        brand: String,
        brandHeader: String,
        clientId: String,
        calculatorComponent: String,
        modelsData: Array,
        dealersData: Object
    },
    setup (props) {
        const { getCurrency } = useMixin()
        const selectedModel = ref(window.passedData && window.passedData.model ? window.passedData.model : '')
        const selectedSubmodel = ref(window.passedData && window.passedData.submodel ? window.passedData.submodel : '')
        const selectedSubmodelObject = ref({})
        const selectedModelSubmodels = ref([])
        const models = reactive([])
        const contactPageIntro = CONSTANTS.TEXTS.CONTACT_PAGE_INTRO
        const contactPageText2 = CONSTANTS.TEXTS.CONTACT_PAGE_2
        const firstName = ref('')
        const lastName = ref('')
        const phone = ref('')
        const email = ref('')
        const showErrors = ref(false)
        const submissionInProgress = ref(false)
        const submissionSuccessful = ref(false)
        const selectedDealer = ref('')
        const dealers = ref([])
        const contactUrl = process.env.VUE_APP_ENQUIRY_URL

        const formIsValid = computed(() => {
            return !!(
                firstName.value &&
                lastName.value &&
                email.value &&
                phone.value &&
                selectedDealer.value.label !== ''
            )
        })

        const formValidationDynamic = computed(() => {
            return {
                selectedDealer: !!selectedDealer.value,
                firstName: !!firstName.value,
                lastName: !!lastName.value,
                phone: !!phone.value,
                email: !!email.value
            }
        })

        const modelsArray = computed(() => {
            return [...new Set(toRaw(props.modelsData).map(car => car.Model))]
        })

        const thankYouText = computed(() => `Thank you ${firstName.value} for enquiring about 
            the ${props.brand} ${selectedModel.value} ${selectedSubmodel.value} on iOWN. 
            One of our team members will be in touch with you soon.`
        )

        const cleanSubmodelsArray = computed(() => {
            const cleanSubmodels = selectedModelSubmodels.value.map(function (el) { return el.SubModel })
            return cleanSubmodels
        })

        const checkForm = () => {
            showErrors.value = true

            if (formIsValid.value) {
                showErrors.value = false
                return true
            }
            showErrors.value = true
            return false
        }

        const selectSubmodels = (attr1) => {
            if (!attr1 || !models.value || models.value.indexOf(attr1) < 0) {
                return []
            }
            return toRaw(props.modelsData).filter((el) => el.Model === attr1)
        }

        const submodelChange = async (e) => {
            if (e) {
                selectedSubmodel.value = e
                const newSelectedSubmodelObject =
                    selectedModelSubmodels.value.find((el) => el.SubModel === e)
                selectedSubmodelObject.value = toRaw(newSelectedSubmodelObject)
            } else {
                submodelChange(selectedModelSubmodels.value[0].SubModel)
            }
        }

        function formatCarSubmodel (value) {
            const selectedCar = toRaw(props.modelsData).find(function (el) {
                return el.Model === selectedModel.value && el.SubModel === toRaw(value).label
            })
            return selectedCar && selectedCar.SubModel
                ? { label: selectedCar.SubModel, rrp: selectedCar.RRP }
                : {}
        }

        watch(selectedModel, async (newModel, oldModel) => {
            if (!newModel) {
                selectedModel.value = oldModel
            } else {
                selectedModelSubmodels.value = selectSubmodels(newModel)
                await nextTick()
                if (selectedModelSubmodels.value && selectedModelSubmodels.value[0] && selectedModelSubmodels.value[0].SubModel) {
                    selectedSubmodel.value = selectedModelSubmodels.value[0].SubModel
                }
            }
        })

        const loadModels = () => {
            models.value = toRaw(props.modelsData).length ? toRaw(props.modelsData).map((el) => el.Model) : []
            if (!selectedModel.value) {
                selectedModel.value = models.value[0]
            }

            selectedModelSubmodels.value = selectSubmodels(selectedModel.value)
        }

        const init = () => {
            dealers.value = props.dealersData.dealers.map((el) => {
                return { value: el.code, label: el.label }
            })

            setTimeout(() => loadModels(), 500)
        }

        const sendEnquiry = async () => {
            if (!formIsValid.value || submissionInProgress.value) {
                return false
            }
            submissionInProgress.value = true

            const apiRequestHeaders = {
                'content-type': 'application/json',
                'x-ibm-client-id': props.clientId,
                client_id: props.clientId
            }

            const enquiryPayload = {
                VehicleModel: selectedModel.value,
                VehicleSubModel: selectedSubmodel.value,
                FirstName: firstName.value,
                LastName: lastName.value,
                Email: email.value,
                Phone: phone.value,
                DealershipName: selectedDealer.value,
                RecaptchaToken: window.recaptchaToken
            }

            return await axios
                .post(contactUrl, enquiryPayload, { headers: apiRequestHeaders })
                .finally(() => {
                    submissionSuccessful.value = true
                    submissionInProgress.value = false
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                })
        }

        const onSubmit = () => {
            if (formIsValid.value) {
                sendEnquiry()
            } else {
                showErrors.value = true
            }
        }

        onMounted(() => {
            init()
        })

        return {
            props,
            modelsArray,
            selectedModel,
            selectedSubmodel,
            selectedSubmodelObject,
            selectedModelSubmodels,
            submodelChange,
            formatCarSubmodel,
            getCurrency,
            cleanSubmodelsArray,
            contactPageIntro,
            contactPageText2,
            selectedDealer,
            firstName,
            lastName,
            phone,
            email,
            showErrors,
            formValidationDynamic,
            submissionInProgress,
            submissionSuccessful,
            formIsValid,
            dealers,
            checkForm,
            onSubmit,
            sendEnquiry,
            thankYouText
        }
    }
})
</script>
